import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../AppIcon';
import Button from './Button';
import { avatarService } from '../../services/avatarService';
import { trackButtonClick, trackNavigation } from '../../utils/googleAnalyticsService';

const Header = ({ isAuthenticated: propIsAuthenticated, user: propUser, onLogout: propOnLogout }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [avatarLoading, setAvatarLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Use auth context
  const { 
    user: contextUser, 
    userProfile: contextUserProfile, 
    isAuthenticated: contextIsAuthenticated, 
    loading,
    signOut 
  } = useAuth();
  
  // Determine which auth state to use - props take precedence
  const user = propUser || contextUser;
  const userProfile = contextUserProfile;
  const isAuthenticated = propIsAuthenticated !== undefined ? propIsAuthenticated : contextIsAuthenticated;

  // ✅ NEW: Effect to fetch avatar URL when user profile loads
  useEffect(() => {
    const loadAvatar = async () => {
      if (userProfile?.avatar_url) {
        setAvatarLoading(true);
        try {
          const url = await avatarService?.getAvatarUrl(userProfile?.avatar_url);
          setAvatarUrl(url);
        } catch (error) {
          console.error('Error loading avatar:', error);
          setAvatarUrl(null); // Fallback to default icon
        } finally {
          setAvatarLoading(false);
        }
      } else {
        // Clear avatar if no avatar_url in profile
        setAvatarUrl(null);
      }
    };

    loadAvatar();
  }, [userProfile?.avatar_url]); // Re-run when avatar_url changes

  // Helper function to get display name - extract first name from full_name
  const getDisplayName = () => {
    if (userProfile?.full_name) {
      return userProfile?.full_name?.split(' ')?.[0];
    }
    return user?.email?.split('@')?.[0] || 'User';
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleAccountMenu = () => {
    setIsAccountMenuOpen(!isAccountMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const closeAccountMenu = () => {
    setIsAccountMenuOpen(false);
  };

  const handleNavigation = (destination, linkText) => {
    trackNavigation(destination, linkText, {
      source: 'header',
      user_authenticated: isAuthenticated,
    });
  };

  const handleLogoClick = () => {
    trackButtonClick('logo_click', 'ParkTrip Logo', {
      current_page: location?.pathname,
    });
    handleNavigation('/', 'Logo');
  };

  const handleSignOut = async () => {
    trackButtonClick('sign_out_header', 'Sign Out', {
      user_id: user?.id,
    });
    
    try {
      // Close menus FIRST
      closeAccountMenu();
      closeMobileMenu();
      
      if (propOnLogout) {
        await propOnLogout();
      } else {
        // CRITICAL FIX: signOut now handles navigation via window.location.href
        // No need to call navigate here - the page will reload automatically
        const { error } = await signOut();
        
        if (error) {
          console.error('Logout error:', error);
          alert(`Logout failed: ${error?.message || 'Please try again'}`);
          return;
        }
        
        // Navigation happens automatically in signOut via window.location.href
        // Page will reload and all components will remount with fresh auth state
      }
    } catch (error) {
      console.error('Unexpected logout error:', error);
      alert('An unexpected error occurred during logout. Please try again.');
    }
  };

  const handleSignInClick = () => {
    // Close any open menus first
    closeMobileMenu();
    closeAccountMenu();
    
    // Navigate to sign in page
    navigate('/user-login');
  };

  const isActivePath = (path) => {
    return location?.pathname === path;
  };

  const Logo = () => (
    <Link to="/" className="flex items-center gap-2 group" onClick={handleLogoClick}>
      <img 
        src="/assets/images/a2995f91-6b95-4160-9806-a16930ef7a5b-1764087901011.png" 
        alt="ParkTrip Planner AI logo featuring friendly bear ranger mascot with transparent background"
        className="h-32 w-auto object-contain"
      />
      <div className="relative flex items-center gap-1.5 px-3 py-1 bg-gradient-to-r from-orange-600/20 via-orange-700/20 to-orange-600/20 border border-orange-600/30 rounded-full shadow-sm hover:shadow-md transition-all duration-300 hover:scale-105">
        <Icon name="Sparkles" size={14} className="text-orange-600" />
        <span className="text-xs font-body font-body-bold text-orange-600 uppercase tracking-wider">Beta</span>
        <div className="absolute inset-0 bg-gradient-to-r from-orange-600/0 via-orange-600/10 to-orange-600/0 rounded-full animate-pulse"></div>
      </div>
    </Link>
  );

  const NavigationItems = ({ mobile = false }) => (
    <>
      <Link
        to="/ai-trip-planning-interface"
        className={`${mobile ? 'block px-4 py-3 text-base' : 'px-4 py-2 text-sm'} font-body font-body-medium rounded-organic-sm transition-organic hover:bg-muted ${
          isActivePath('/ai-trip-planning-interface') ? 'bg-primary text-primary-foreground' : 'text-foreground hover:text-primary'
        }`}
        onClick={mobile ? closeMobileMenu : undefined}
      >
        Plan Trip
      </Link>
      
      {isAuthenticated && (
        <Link
          to="/user-dashboard"
          className={`${mobile ? 'block px-4 py-3 text-base' : 'px-4 py-2 text-sm'} font-body font-body-medium rounded-organic-sm transition-organic hover:bg-muted ${
            isActivePath('/user-dashboard') || isActivePath('/trip-detail-view') ? 'bg-primary text-primary-foreground' : 'text-foreground hover:text-primary'
          }`}
          onClick={mobile ? closeMobileMenu : undefined}
        >
          My Trips
        </Link>
      )}
    </>
  );

  // ✅ UPDATED: Component to render user avatar (image or icon fallback)
  const UserAvatar = ({ size = 8 }) => {
    const sizeClasses = {
      8: 'w-8 h-8',
      10: 'w-10 h-10'
    };
    
    const iconSizes = {
      8: 16,
      10: 20
    };

    if (avatarLoading) {
      return (
        <div className={`${sizeClasses?.[size]} bg-primary rounded-full flex items-center justify-center`}>
          <Icon name="Loader" size={iconSizes?.[size]} color="white" className="animate-spin" />
        </div>
      );
    }

    if (avatarUrl) {
      return (
        <img
          src={avatarUrl}
          alt={`${getDisplayName()}'s profile picture`}
          className={`${sizeClasses?.[size]} rounded-full object-cover border-2 border-primary`}
          onError={() => setAvatarUrl(null)} // Fallback to icon if image fails to load
        />
      );
    }

    // Fallback to icon
    return (
      <div className={`${sizeClasses?.[size]} bg-primary rounded-full flex items-center justify-center`}>
        <Icon name="User" size={iconSizes?.[size]} color="white" />
      </div>
    );
  };

  const AccountMenu = ({ mobile = false }) => {
    if (!isAuthenticated) {
      return (
        <div className={mobile ? 'px-4 py-2 space-y-2' : 'flex items-center space-x-2'}>
          <Button 
            variant="ghost" 
            size={mobile ? 'default' : 'sm'} 
            className={mobile ? 'w-full justify-start' : ''}
            onClick={handleSignInClick}
          >
            Sign In
          </Button>
          <Link to="/user-registration" onClick={mobile ? closeMobileMenu : undefined}>
            <Button variant="default" size={mobile ? 'default' : 'sm'} className={mobile ? 'w-full' : ''}>
              Sign Up
            </Button>
          </Link>
        </div>
      );
    }

    if (mobile) {
      return (
        <div className="px-4 py-2 border-t border-border">
          <div className="py-3">
            <div className="flex items-center space-x-3">
              <UserAvatar size={8} />
              <div>
                <p className="font-body font-body-medium text-sm text-foreground">
                  {getDisplayName()}
                </p>
                <p className="font-caption text-xs text-muted-foreground">
                  {user?.email || 'user@example.com'}
                </p>
              </div>
            </div>
          </div>
          <div className="space-y-1">
            <Link
              to="/user-profile-management"
              className="block px-3 py-2 text-sm font-body text-foreground hover:bg-muted rounded-organic-sm transition-organic"
              onClick={closeMobileMenu}
            >
              Profile Settings
            </Link>
            <button
              onClick={handleSignOut}
              disabled={loading}
              className="block w-full text-left px-3 py-2 text-sm font-body text-foreground hover:bg-muted rounded-organic-sm transition-organic disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Signing Out...' : 'Sign Out'}
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="relative">
        <button
          onClick={toggleAccountMenu}
          className="flex items-center space-x-2 px-3 py-2 rounded-organic-sm hover:bg-muted transition-organic"
        >
          <UserAvatar size={8} />
          <Icon name="ChevronDown" size={16} className={`transition-transform ${isAccountMenuOpen ? 'rotate-180' : ''}`} />
        </button>

        {isAccountMenuOpen && (
          <>
            <div className="fixed inset-0 z-10" onClick={closeAccountMenu} />
            <div className="absolute right-0 mt-2 w-56 bg-popover border border-border rounded-organic shadow-organic-lg z-20">
              <div className="p-3 border-b border-border">
                <div className="flex items-center space-x-3">
                  <UserAvatar size={10} />
                  <div>
                    <p className="font-body font-body-medium text-sm text-popover-foreground">
                      {getDisplayName()}
                    </p>
                    <p className="font-caption text-xs text-muted-foreground">
                      {user?.email || 'user@example.com'}
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-2">
                <Link
                  to="/user-profile-management"
                  className="flex items-center space-x-2 px-3 py-2 text-sm font-body text-popover-foreground hover:bg-muted rounded-organic-sm transition-organic"
                  onClick={closeAccountMenu}
                >
                  <Icon name="Settings" size={16} />
                  <span>Profile Settings</span>
                </Link>
                <button
                  onClick={handleSignOut}
                  disabled={loading}
                  className="flex items-center space-x-2 w-full px-3 py-2 text-sm font-body text-popover-foreground hover:bg-muted rounded-organic-sm transition-organic disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Icon name="LogOut" size={16} />
                  <span>{loading ? 'Signing Out...' : 'Sign Out'}</span>
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Logo />

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            <NavigationItems />
          </div>

          {/* Desktop Account Menu */}
          <div className="hidden md:flex items-center">
            <AccountMenu />
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button
              onClick={toggleMobileMenu}
              className="p-2 rounded-organic-sm text-foreground hover:bg-muted transition-organic"
              aria-label="Toggle mobile menu"
            >
              <Icon name={isMobileMenuOpen ? "X" : "Menu"} size={24} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-background border-b border-border">
          <div className="px-4 py-2 space-y-1">
            <NavigationItems mobile />
          </div>
          <AccountMenu mobile />
        </div>
      )}
    </header>
  );
};

export default Header;